import React from 'react'
import {
  styled,
  Button as MuiButton,
  Typography,
  Dialog,
  Paper,
  useTheme,
  useMediaQuery,
  DialogProps,
  Fade,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  DialogActions,
  PaperProps,
  SxProps,
  Theme,
  Box,
} from '@mui/material'
import MaterialSymbol from './MaterialSymbol'
import { shouldForwardProp } from '../util/helpers'
import { colors } from '../themes/colors'
import { createPortal } from 'react-dom'

export const DialogContent = styled(MuiDialogContent, {
  shouldForwardProp: (prop) => shouldForwardProp(['scrollable'], prop),
})<{ scrollable?: boolean }>`
  padding: 24px 32px;
  overflow-x: visible;
  width: 100%;
  overflow-y: ${({ scrollable }) => (scrollable ? 'auto' : 'hidden')};
`
// NOTE: use overflow-y: auto instead of 'scroll', otherwise it will reserved the space for scroll even the content is not scrollable

export const StyledPaper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'white',
  // padding: '8px 16px',
  // gap: '24px',
  borderRadius: '12px',
  width: '100%',
  overflowX: 'hidden',

  [theme.breakpoints.down('sm')]: {
    padding: '0',
  },
}))

export const DialogTitle = styled(MuiDialogTitle)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    fontSize: 'calc(min(max(17px, 6vw), 24px))',
  },
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
}))

export const HeaderContaier = styled('div')<{ fullscreen?: boolean; divider?: boolean }>(
  ({ fullscreen, divider, theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '24px 32px 16px 32px',
    marginTop: fullscreen ? 'var(--safe-area-statusbarHeight)' : '',
    borderBottom: divider ? `1px solid ${theme.colors.primary100}` : '',
  }),
)

export const BackButton = styled(MuiButton)(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.colors.primary500,
  padding: '0 0 0 0',
  border: 'none',
  lineHeight: '150%',
  textDecoration: 'none',
  textUnderlineOffset: '2px',

  '&:hover': {
    border: 0,
    background: 'inherit',
    color: theme.colors.primary500,
    '& .MuiTypography-root': {
      color: 'inherit',
      textDecoration: 'underline',
    },
  },
  '&:active': {
    color: theme.colors.primary900,
  },
  '& .MuiTypography-root': {
    color: 'inherit',
  },
}))

export const CloseButton = styled(MuiButton)(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.colors.grey700,
  padding: '0 0 0 0',
  border: 'none',
  lineHeight: '150%',
  textDecoration: 'none',
  textUnderlineOffset: '2px',
  marginLeft: 'auto',

  '&:hover': {
    border: 0,
    background: 'inherit',
    color: theme.colors.primary500,
    '& .MuiTypography-root': {
      color: 'inherit',
      textDecoration: 'underline',
    },
  },
  '&:active': {
    color: theme.colors.primary900,
  },
  '& .MuiTypography-root': {
    color: 'inherit',
  },
}))

export interface ModalProps extends Omit<DialogProps, 'title'> {
  handleClose: () => void
  testId?: string
  header?: React.ReactNode
  customHeader?: React.ReactNode // this will replace the modal header includes title and close button
  footer?: React.ReactNode
  scrollRef?: React.Ref<unknown>
  paperProps?: Partial<PaperProps>
  scrollable?: boolean
  title?: string | React.ReactNode
  headerDivider?: boolean
  footerDivider?: boolean
  contentBg?: keyof typeof colors
  dialogContentSx?: SxProps<Theme>
}

export const Modal = ({
  open,
  title,
  children,
  handleClose,
  testId,
  header,
  customHeader,
  footer,
  scrollRef,
  scrollable = true,
  paperProps,
  headerDivider,
  footerDivider,
  contentBg,
  dialogContentSx,
  ...props
}: ModalProps) => {
  const theme = useTheme()
  const isSm = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Dialog
      scroll='paper'
      fullScreen={isSm || props.fullScreen}
      PaperComponent={StyledPaper}
      PaperProps={paperProps}
      TransitionComponent={Fade}
      open={open}
      onClose={handleClose}
      aria-labelledby={`modal-${testId}`}
      slotProps={{
        backdrop: {
          id: `${testId}--close-backdrop`,
          //eslint-disable-next-line
          //@ts-ignore
          'data-testid': `${testId}--close-backdrop`,
          timeout: 500,
        },
      }}
      data-testid={testId}
      {...props}
    >
      {customHeader ? (
        <HeaderContaier divider={headerDivider} fullscreen={isSm || props.fullScreen}>
          {customHeader}
        </HeaderContaier>
      ) : (
        <HeaderContaier divider={headerDivider} fullscreen={isSm || props.fullScreen}>
          <DialogTitle>
            {title}
            <CloseButton
              id={`${testId}--close-button`}
              data-testid={`${testId}--close`}
              variant='outlined'
              disableTouchRipple
              onClick={handleClose}
            >
              <Typography variant='body2'>Close</Typography>
              <MaterialSymbol>close</MaterialSymbol>
            </CloseButton>
          </DialogTitle>
          {header}
        </HeaderContaier>
      )}
      <DialogContent
        scrollable={scrollable}
        ref={scrollRef}
        sx={{
          mb: 0,
          ml: 0,
          background: contentBg ? theme.colors[contentBg] : 'white',
          ...dialogContentSx,
        }}
      >
        {children}
      </DialogContent>
      <Box
        id='dialog-action-portal'
        sx={{ padding: '0px !important', margin: '0px 0px -24px 0px' }}
      />
      <DialogActions
        sx={{
          borderTop: footerDivider ? `1px solid ${colors.primary200}` : 'none',
          padding: footer ? '16px 32px' : '0px 32px 24px 32px',
          mt: footer ? '24px' : '0px',
        }}
      >
        {footer}
      </DialogActions>
    </Dialog>
  )
}

interface ModalActionsProps {
  children: React.ReactNode
  showDivider?: boolean
}

export const ModalActions: React.FC<ModalActionsProps> = ({ children, showDivider }) => {
  const footerElement = document.getElementById('dialog-action-portal')

  if (!footerElement) {
    console.error("The element with id 'dialog-actions' was not found.")
    return null // Safeguard against missing element
  }

  return createPortal(
    <DialogActions
      id='dialog-actions'
      sx={{
        width: '100%',
        borderTop: showDivider ? `1px solid ${colors.primary200}` : 0,
        padding: '16px 32px',
      }}
    >
      {children}
    </DialogActions>,
    footerElement,
  )
}

export default Modal
